import get from 'lodash/get'
import truncate from 'lodash/truncate'
import isString from 'lodash/isString'
import startsWith from 'lodash/startsWith'
import strictUriEncode from 'strict-uri-encode'
import { List, fromJS } from 'immutable'
import {
  post as apiPost,
  get as apiGet,
  put as apiPut,
  del as apiDelete,
  TYPE_BROOKLYN_JSON,
} from 'api-client'
import { ICON_TYPES } from 'components/Icon.v2'
import {
  URL_PLAYLIST_FAVORITES,
  URL_PLAYLIST_WATCH_HISTORY,
  URL_PLAYLIST_WATCH_LATER,
} from 'services/url/constants'
import {
  STORE_KEY_PLAYLIST_DEFAULT,
  STORE_KEY_PLAYLIST_FAVORITES,
  STORE_KEY_WATCH_HISTORY,
  STORE_KEY_PLAYLIST_USER,
} from 'services/store-keys'

export const PLAYLIST_EDIT_COMPLETE = 'playlistEditComplete'

export const PLAYLIST_TYPE_DEFAULT = 'default'
export const PLAYLIST_TYPE_FAVORITES = 'favorites'
export const PLAYLIST_TYPE_HISTORY = 'watch-history'
export const PLAYLIST_TYPE_USER = 'user'
export const PLAYLIST_TYPE_PORTAL = 'portal'
export const PLAYLIST_TYPE_OFFLINE = 'offline'
export const PLAYLIST_ERROR_EXISTING = 'existing'
export const PLAYLIST_PORTAL_LIMIT = 15

export async function getPlaylistItem ({ auth, contentId }) {
  const url = `v1/playlists/content/${contentId}`
  const result = await apiGet(url, null, { auth }, TYPE_BROOKLYN_JSON)
  return get(result, ['body'], {})
}

export async function updatePlaylist ({ auth, type, name }) {
  const url = `v2/playlists/${type}`
  const result = await apiPut(url, { name }, { auth }, TYPE_BROOKLYN_JSON)
  return get(result, ['body'], {})
}

export async function updatePlaylistItem ({
  auth, contentId, type, add,
}) {
  const action = add ? 'add' : 'remove'
  const url = `v2/playlists/${type}/${action}/${strictUriEncode(contentId)}`
  const result = await apiPost(url, null, { auth }, TYPE_BROOKLYN_JSON)
  const handledRes = handleInPlaylistResponse(result)
  return handledRes ? get(result, ['body'], {}) : {}
}

export async function updatePlaylistItemPosition ({
  auth,
  contentId,
  playlistId,
  playlistPosition,
}) {
  const url = `v3/playlists/content/${strictUriEncode(contentId)}`

  const result = await apiPut(
    url,
    {
      playlistId,
      playlistPosition,
      playlistType: 'portal',
    },
    {
      auth,
    },
    TYPE_BROOKLYN_JSON,
  )
  return get(result, ['body'], {})
}

// ------------------------------------
// OLD functions used on PlaylistPage
// ------------------------------------

export function handleInPlaylistResponse (res) {
  const success = get(res, ['body', 'success'])
  if (!success) {
    throw new Error('The playlist information failed to save. Please try again.')
  }
  return success
}

export function setInPlaylist (options) {
  const { id, inPlaylist, auth } = options
  // if inPlaylist is true, add to playlist, otherwise remove
  const action = inPlaylist ? 'add' : 'remove'
  const url = `v2/playlists/default/${action}/${strictUriEncode(id)}`
  return apiPost(url, null, { auth }, TYPE_BROOKLYN_JSON)
    .then((res) => {
      return handleInPlaylistResponse(res)
    })
}

// ------------------------------------
// Fetch Portal Playlists Dropdown
// ------------------------------------
export async function getWatchLaterPlaylist ({ auth, page = 1, limit }) {
  // Endpoint pagination returns 0 as the first page by default.
  // We do not want user's to see this so we control the offset here
  const offsetPage = page - 1

  const res = await apiGet(
    '/v2/playlists/default',
    { p: offsetPage, pp: limit },
    { auth },
    TYPE_BROOKLYN_JSON,
  )

  const body = get(res, 'body', {})
  const results = {
    ...body,
    currentPage: get(body, 'currentPage') + 1,
  }
  return results
}

export async function getRecentlyWatchedPlaylist ({ auth, page = 1, limit }) {
  // Endpoint pagination returns 0 as the first page by default.
  // We do not want user's to see this so we control the offset here
  const offsetPage = page - 1

  const res = await apiGet(
    '/v2/user/recently-watched',
    { p: offsetPage, pp: limit },
    { auth },
    TYPE_BROOKLYN_JSON,
  )

  const body = get(res, 'body', {})
  const results = {
    ...body,
    currentPage: get(body, 'currentPage') + 1,
  }
  return results
}

export async function getFavoritesPlaylist ({ auth, page = 1, limit }) {
  // Endpoint pagination returns 0 as the first page by default.
  // We do not want user's to see this so we control the offset here
  const offsetPage = page - 1

  const res = await apiGet(
    '/v2/playlists/favorites',
    { p: offsetPage, pp: limit },
    { auth },
    TYPE_BROOKLYN_JSON,
  )

  const body = get(res, 'body', {})
  const results = {
    ...body,
    currentPage: get(body, 'currentPage') + 1,
  }
  return results
}

export async function getCustomPlaylist ({
  auth, page = 1, limit, playlistType,
}) {
  // Endpoint pagination returns 0 as the first page by default.
  // We do not want user's to see this so we control the offset here
  const offsetPage = page - 1

  const res = await apiGet(
    `/v2/playlists/${playlistType}`,
    { p: offsetPage, pp: limit },
    { auth },
    TYPE_BROOKLYN_JSON,
  )

  const body = get(res, 'body', {})
  const results = {
    ...body,
    currentPage: get(body, 'currentPage') + 1,
  }
  return results
}

// ------------------------------------
// Specify the standard playlists that all users must have
// ------------------------------------
export const BASE_PLAYLISTS = [
  {
    id: 'watch-later',
    type: PLAYLIST_TYPE_DEFAULT,
    staticTextKey: 'watchLater',
    tileStoreKey: STORE_KEY_PLAYLIST_DEFAULT,
    url: URL_PLAYLIST_WATCH_LATER,
    iconType: ICON_TYPES.CLOCK_FILL,
    name: 'Watch Later',
  },
  {
    id: 'favorites',
    type: PLAYLIST_TYPE_FAVORITES,
    staticTextKey: 'favorites',
    tileStoreKey: STORE_KEY_PLAYLIST_FAVORITES,
    url: URL_PLAYLIST_FAVORITES,
    iconType: ICON_TYPES.STAR_FILL,
    name: 'Favorites',
  },
  {
    id: 'watch-history',
    type: PLAYLIST_TYPE_HISTORY,
    staticTextKey: 'watchHistory',
    tileStoreKey: STORE_KEY_WATCH_HISTORY,
    url: URL_PLAYLIST_WATCH_HISTORY,
    iconType: ICON_TYPES.HOURGLASS,
    name: 'Watch History',
  },
]

export const PORTAL_PLAYLIST = {
  id: 'portal',
  name: 'Portal',
  type: PLAYLIST_TYPE_PORTAL,
}

/**
 * Get the base playlists as a List from Immutable.js
 *
 * @param {boolean} includePortal
 * @returns {object}
 */
export function getImmutableBasePlaylists (includePortal = false) {
  const playlists = BASE_PLAYLISTS.filter((pl) => pl.type !== PLAYLIST_TYPE_HISTORY)
  if (includePortal) {
    playlists.push(PORTAL_PLAYLIST)
  }
  return fromJS(playlists)
}

// ------------------------------------
// Fetch Playlists (default + custom)
// ------------------------------------
export async function getAllUserPlaylists ({ auth }) {
  const res = await apiGet(
    '/v2/playlists',
    null,
    { auth },
    TYPE_BROOKLYN_JSON,
  )

  const body = get(res, 'body', {})
  const results = {
    ...body,
  }
  return results
}

/**
 * Get the store key where tiles are located by playlistType
 */
export const getPlaylistTileStoreKey = (playlistType) => {
  if (startsWith(playlistType || '', 'user-')) {
    return STORE_KEY_PLAYLIST_USER
  }

  return get(BASE_PLAYLISTS.find((pl) => pl.type === playlistType), 'tileStoreKey', '')
}

export async function createUserPlaylist ({ auth, params }) {
  const baseParams = { type: 'user' }

  const res = await apiPost(
    '/v2/playlists/create',
    { ...params, ...baseParams },
    { auth },
    TYPE_BROOKLYN_JSON,
  )

  if (!res.ok) { // Status 404, 500, etc
    throw new Error(`${res.statusCode} Failed to create `)
  }

  if (res.statusCode === 200) { // Existing
    return {
      existing: true,
    }
  }

  const body = get(res, 'body', {})
  const results = {
    existing: null,
    data: { ...body },
  }
  return results
}

// Fetch delete quese ids - used when editing a playlist
export function getDeleteQueueIds (playlistStoreKey, playlist) {
  return playlist
    .getIn([playlistStoreKey, 'queue'], List())
    .reduce((reduction, v) => {
      if (v !== undefined && v.get('playlist') === false) {
        // eslint-disable-next-line no-param-reassign
        reduction = reduction.push(v.get('id'))
      }
      return reduction
    }, List())
}

// Delete playlist items
export async function deleteItemsFromPlaylist ({
  deleteIds,
  playlistType,
  auth,
} = {}) {
  await deleteIds.map((id) => {
    const url = `/v2/playlists/${playlistType}/remove/${strictUriEncode(id)}`
    return apiPost(url, null, { auth n    }
  }
  // Mimic and HTTP response shape putting the data on body
  const body = {
    facetValues: data.facetValues.map((item) => ({
      ...item,
      value: SORT_OPTIONS[item.value],
      path: null,
    })),
  }

  return { body }
}
